import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { RootState } from "@/store";
import $http from "@/services/Http";
import {
  downloadRequestHeaders,
  downloadResponse,
  settleDownload,
} from "@/utils/Download";
import File from "@/models/File";
import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios";
import FileService from "@/services/fileService";
import { ToastDanger } from "@/toast/ToastProgrammatic";
import { defaultErrorHandler } from "@/utils/Error";

export enum MutationTypes {
  StartDownload = "StartDownload",
  UpdateProgress = "UpdateProgress",
  RemoveDownload = "RemoveDownload",
  ClearDownloadHistory = "ClearDownloadHistory",
}

function handleDownloadFailure(error: unknown, name: string): void {
  if (axios.isAxiosError(error) && !error.response) {
    ToastDanger.open(
      `Could not download "${name}". Check your connection and try again.`
    );

    return;
  }

  defaultErrorHandler(error as Error);
}

export interface DownloadProgress {
  id: symbol;
  started_at: Date;
  name: string;
  progress: number;
}

export interface IDownloadState {
  downloads: DownloadProgress[];
}

const state: IDownloadState = {
  downloads: [],
};

const getters: GetterTree<IDownloadState, RootState> = {
  activeDownloads: (state): DownloadProgress[] =>
    state.downloads.filter((d) => d.progress < 100),
  completedDownloads: (state): DownloadProgress[] =>
    state.downloads.filter((d) => d.progress === 100),
  recentDownloads: (state): DownloadProgress[] =>
    state.downloads
      .slice(0)
      .sort((a, b) => {
        const diffProgress = b.progress - a.progress;

        if (diffProgress !== 0) {
          return diffProgress;
        }

        return b.started_at.getTime() - a.started_at.getTime();
      })
      .slice(0, 8),
};

type ApiDownloadCallback = (config: AxiosRequestConfig) => AxiosPromise;
export interface ApiDownloadPayload {
  callback: ApiDownloadCallback;
  name: string;
}

const actions: ActionTree<IDownloadState, RootState> = {
  queueFileForDownload(
    { commit, state, dispatch },
    file: File
  ): Promise<AxiosResponse | undefined> {
    return settleDownload(new FileService(file).resolveUrl(), (error) =>
      handleDownloadFailure(error, file.name)
    ).then((url) =>
      url ? dispatch("queueForDownload", { url, name: file.name }) : undefined
    );
  },
  queueForDownload(
    { commit, state },
    {
      url,
      name,
      config = {},
    }: { url: string; name: string; config: AxiosRequestConfig }
  ): Promise<AxiosResponse | undefined> {
    const id = Symbol();
    commit(MutationTypes.StartDownload, {
      id,
      name,
      started_at: new Date(),
      progress: 0,
    });

    config = {
      ...config,
      headers: downloadRequestHeaders(url, config.headers),
    };

    const request = $http
      .get(
        url,
        Object.assign(
          {
            responseType: "blob",
            onDownloadProgress: (event: ProgressEvent) => {
              if (event.type !== "progress") {
                return;
              }

              commit(MutationTypes.UpdateProgress, {
                id,
                progress: (event.loaded / event.total) * 100,
              });
            },
          },
          config
        )
      )
      .then((response) => {
        const match = name.match(/\.(\w+)\?/) || [];
        downloadResponse(
          response,
          name,
          match[1] || null //Force extension to be the same as the one found in the url
        );

        return response;
      });

    return settleDownload(request, (error) => {
      commit(MutationTypes.RemoveDownload, id);
      handleDownloadFailure(error, name);
    });
  },
  queueApiCallForDownload(
    { commit, state },
    { callback, name }: ApiDownloadPayload
  ): Promise<AxiosResponse | undefined> {
    const id = Symbol();
    commit(MutationTypes.StartDownload, {
      id,
      name,
      started_at: new Date(),
      progress: 0,
    });

    const request = callback({
      responseType: "blob",
      onDownloadProgress: (event: ProgressEvent) => {
        if (event.type !== "progress") {
          return;
        }

        commit(MutationTypes.UpdateProgress, {
          id,
          progress: (event.loaded / event.total) * 100,
        });
      },
    }).then((response) => {
      downloadResponse(response, name);

      return response;
    });

    return settleDownload(request, (error) => {
      commit(MutationTypes.RemoveDownload, id);
      handleDownloadFailure(error, name);
    });
  },
};

const mutations: MutationTree<IDownloadState> = {
  [MutationTypes.StartDownload](state, payload: DownloadProgress) {
    state.downloads.push(payload);
  },
  [MutationTypes.UpdateProgress](
    state,
    payload: Exclude<DownloadProgress, "name" | "started_at">
  ) {
    const download = state.downloads.find((d) => d.id === payload.id);

    if (!download) {
      return;
    }

    download.progress = payload.progress;
  },
  [MutationTypes.RemoveDownload](state, id: symbol) {
    state.downloads = state.downloads.filter((d) => d.id !== id);
  },
  [MutationTypes.ClearDownloadHistory](state) {
    state.downloads = state.downloads.filter((d) => d.progress < 100);
  },
};

export const downloads: Module<IDownloadState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
